// Patient risk signals card — score, band, and trend metrics.

import type { JSX, ReactNode } from "react";

// All fields are nullable at runtime: nsofar-sync patient stubs (and partially-filled patients)
// store null for risk/adherence/trend metrics. Guards below use `!= null` (not `!== undefined`)
// so a null never reaches `.toFixed()` / arithmetic — the "Cannot read properties of null" crash.
interface PatientRiskProps {
  risk_score?: number | null;
  risk_score_band?: string | null;
  days_since_last_visit?: number | null;
  fall_frequency_90d?: number | null;
  adherence_rate_critical_meds_30d?: number | null;
  weight_change_pct_90d?: number | null;
  mood_decline_trend?: boolean | null;
  bp_trend_30d?: string | null;
  cognitive_vs_last_visit?: string | null;
}

const RISK_COLOR = (score: number): string =>
  score < 40 ? "text-emerald-700" : score <= 70 ? "text-amber-600" : "text-red-600";

const BAND_PILL: Record<string, string> = {
  low:      "bg-emerald-100 text-emerald-700",
  moderate: "bg-amber-100 text-amber-700",
  high:     "bg-red-100 text-red-700",
  critical: "bg-rose-100 text-rose-800",
};

function fmtMoodDecline(v: boolean | null | undefined): string { return v != null ? (v ? "Yes" : "No") : "—"; }
// adherence_rate_critical_meds_30d is stored 0–100 (DRS §H — percentage, not fraction).
function fmtPct(v: number | null | undefined): string { return v != null ? `${v.toFixed(0)}%` : "—"; }

function MetricTile({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <div className="text-sm font-semibold text-slate-800">{children}</div>
    </div>
  );
}

export function PatientRiskCard({ p }: { p: PatientRiskProps }): JSX.Element {
  const band = p.risk_score_band ?? "";
  const score = p.risk_score;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Risk Signals</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <MetricTile label="Risk score">
          {score != null ? <span className={RISK_COLOR(score)}>{score}</span> : "—"}
        </MetricTile>
        <MetricTile label="Risk band">
          {band
            ? <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${BAND_PILL[band] ?? "bg-slate-100 text-slate-600"}`}>{band}</span>
            : "—"}
        </MetricTile>
        <MetricTile label="Days since last visit">{p.days_since_last_visit ?? "—"}</MetricTile>
        <MetricTile label="Fall freq (90d)">{p.fall_frequency_90d ?? "—"}</MetricTile>
        <MetricTile label="Adherence rate (30d)">{fmtPct(p.adherence_rate_critical_meds_30d)}</MetricTile>
        <MetricTile label="Weight change % (90d)">{p.weight_change_pct_90d != null ? `${p.weight_change_pct_90d}%` : "—"}</MetricTile>
        <MetricTile label="Mood decline trend">{fmtMoodDecline(p.mood_decline_trend)}</MetricTile>
        <MetricTile label="BP trend (30d)">{p.bp_trend_30d ?? "—"}</MetricTile>
        <MetricTile label="Cognitive vs last visit">{p.cognitive_vs_last_visit ?? "—"}</MetricTile>
      </div>
    </div>
  );
}
